import React from "react";
import { Badge } from "@/components/ui";
import { useTranslation } from "react-i18next";

interface InterestsBadgeListProps {
  interests?: string[];
  limit?: number;
  colored?: boolean;
}

const badgeColorStyles = [
  {
    text: "text-orange-600",
    bg: "bg-orange-600/10",
    border: "border-orange-600/50",
  },
  {
    text: "text-blue-600",
    bg: "bg-blue-600/10",
    border: "border-blue-600/50",
  },
  {
    text: "text-green-600",
    bg: "bg-green-600/10",
    border: "border-green-600/50",
  },
  {
    text: "text-purple-600",
    bg: "bg-purple-600/10",
    border: "border-purple-600/50",
  },
];

export const InterestsBadgeList: React.FC<InterestsBadgeListProps> = ({
  interests,
  limit = 4,
  colored = false,
}) => {
  const { t } = useTranslation();

  if (!interests || interests.length === 0) {
    return (
      <Badge
        className="rounded-full px-[11px] py-[3px] text-xs text-muted-foreground"
        variant="dashed"
        role="listitem"
      >
        {t("studentCard.noInterests")}
      </Badge>
    );
  }

  const displayedInterests = interests.slice(0, limit);
  const remainingInterests = interests.length - limit;

  return (
    <div
      className="flex flex-wrap gap-2"
      role="list"
      aria-label={t("studentCard.gridView.interests")}
    >
      {displayedInterests.map((interest, i) => {
        if (colored) {
          const color = badgeColorStyles[i % badgeColorStyles.length];
          return (
            <Badge
              key={i}
              className={`${color.text} ${color.bg} ${color.border} rounded-xl border`}
              role="listitem"
            >
              {interest}
            </Badge>
          );
        }
        return (
          <Badge key={i} variant="outline" role="listitem">
            {interest}
          </Badge>
        );
      })}
      {remainingInterests > 0 && (
        <Badge variant="outline" className="text-foreground rounded-full" role="listitem">
          +{remainingInterests}
        </Badge>
      )}
    </div>
  );
};
